import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';

const CoursesHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Course Browsing' }
  ];

  return (
    <HelpLayout title="Course Browsing & Search" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Course Browsing & Search Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6
            }}>
              This comprehensive guide will help you find and explore training programs that match your interests and career goals.
            </p>

            {/* Quick Start Section */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--primary-yellow-light)',
              borderRadius: '8px',
              marginTop: '24px',
              border: '1px solid var(--primary-yellow)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚀 Quick Start
              </h3>
              <p style={{ color: 'var(--text-gray)', margin: 0, lineHeight: 1.6 }}>
                Navigate to the <strong>Courses</strong> page from the main menu to start browsing available training programs.
                Use the search bar and filters to find programs that match your interests and qualifications.
              </p>
            </div>

            {/* Search and Filtering Section */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🔍 Search and Filtering
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '8px'
                }}>
                  Search by Keywords
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Use the search bar to find courses by:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Program Name:</strong> Search for specific course titles</li>
                  <li><strong>Trade Specialisation:</strong> Find courses in your field of interest</li>
                  <li><strong>Description Keywords:</strong> Search course content and objectives</li>
                  <li><strong>Skills:</strong> Look for courses that teach specific skills</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '8px'
                }}>
                  Filter Options
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📍 Campus Location
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Filter by training venue to find courses near you
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      🎓 NQF Level
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Filter by qualification level (1-10) based on your education
                    </p>
                  </div>
                </div>
              </div>
            {/* Course Information Section */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📋 Understanding Course Information
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Course Card Overview
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Each course card displays essential information at a glance:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Program Name:</strong> Official course title</li>
                  <li><strong>Campus:</strong> Training location and venue</li>
                  <li><strong>Duration:</strong> Course length in months</li>
                  <li><strong>NQF Level:</strong> Qualification framework level</li>
                  <li><strong>Credits:</strong> Academic credit value</li>
                  <li><strong>Capacity:</strong> Available spaces vs. total capacity</li>
                  <li><strong>Status:</strong> Application availability</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Detailed Course Information
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Click on any course card to expand and view detailed information:
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📖 Course Description
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Detailed overview of what you'll learn and course objectives
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      ✅ Entry Requirements
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Minimum qualifications and prerequisites needed
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      🎯 Learning Outcomes
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Skills and knowledge you'll gain upon completion
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      💼 Career Opportunities
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Job prospects and career paths after completion
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Wishlist and Application Section */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                💝 Wishlist and Quick Actions
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Adding Courses to Your Wishlist
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Save interesting courses for later consideration:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li>Click the heart icon (♡) on any course card</li>
                  <li>View your saved courses in the Dashboard wishlist section</li>
                  <li>Receive notifications about application deadlines</li>
                  <li>Quick access to apply when you're ready</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Application Status Indicators
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#e8f5e8',
                    borderRadius: '6px',
                    border: '1px solid #4ade80'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>
                      ✅ Available
                    </span>
                    <p style={{ fontSize: '12px', color: '#16a34a', margin: '4px 0 0 0' }}>
                      Open for applications
                    </p>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#d97706' }}>
                      ⏳ Applied
                    </span>
                    <p style={{ fontSize: '12px', color: '#d97706', margin: '4px 0 0 0' }}>
                      Application submitted
                    </p>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #ef4444'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                      ❌ Full
                    </span>
                    <p style={{ fontSize: '12px', color: '#dc2626', margin: '4px 0 0 0' }}>
                      Capacity reached
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tips and Best Practices */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💡 Tips for Effective Course Browsing
              </h3>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                margin: 0
              }}>
                <li><strong>Complete your profile first</strong> - This helps match you with suitable courses</li>
                <li><strong>Check entry requirements</strong> - Ensure you meet the minimum qualifications</li>
                <li><strong>Consider location and schedule</strong> - Choose courses that fit your availability</li>
                <li><strong>Read course descriptions thoroughly</strong> - Understand what you'll be learning</li>
                <li><strong>Use the wishlist feature</strong> - Save courses to compare and decide later</li>
                <li><strong>Apply early</strong> - Popular courses fill up quickly</li>
                <li><strong>Check for prerequisites</strong> - Some advanced courses require prior training</li>
              </ul>
            </div>

            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default CoursesHelp;
